import { Badge, Icon, Pressable } from '@rbilabs/universal-components';
import { usePaymentContext } from 'features/payment';
import { CardTypes, IPaymentMethod } from 'features/payment/types';
import { shouldReVaultPaymentMethod } from 'features/payment/utils';
import { StyleSheet, Text, View } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { theme } from 'styles/configure-theme';
import { primitive } from 'styles/constants/primitives';
import { Region } from 'utils/environment/types';

import { PaymentMethodDetails } from '../payment-method-details';
import { PaymentMethodIcon } from '../payment-method-icon';

import { styles as commonStyles } from './styles';
import { SelectionType } from './types';

type SelectorOptionProps = {
  option: IPaymentMethod;
  bordered?: boolean;
  selectedMethod: IPaymentMethod | undefined;
  onSelect: (type: SelectionType, id: string) => void;
  onUpdatePaymentMethod?: (id: string) => void;
};

export function SelectorOption({
  option,
  selectedMethod,
  onSelect,
  bordered,
}: SelectorOptionProps) {
  const { region } = useLocale();
  const isCARegion = region === Region.CA;
  const { isOrbital } = usePaymentContext();
  const showMastercardPartnership = useFlag(LaunchDarklyFlag.SHOW_MASTERCARD_PARTNERSHIP);

  if (option.prepaid && isCARegion) {
    return null;
  }

  const accountIdentifier = option.accountIdentifier ?? option.fdAccountId ?? '';
  const isSelectedMethod =
    accountIdentifier === selectedMethod?.fdAccountId ||
    accountIdentifier === selectedMethod?.accountIdentifier;
  const { isInvalid = false, isExpired = false } = shouldReVaultPaymentMethod(option, isOrbital);
  const isRevaultRequired = isInvalid || isExpired;

  const showMasterCardBadge =
    option.credit?.cardType === CardTypes.MASTERCARD && showMastercardPartnership;

  return (
    <Pressable
      testID={`method-${option.fdAccountId}`}
      style={[
        commonStyles.option,
        styles.option,
        {
          backgroundColor: isRevaultRequired ? primitive.bk.$errorLight : undefined,
          borderBottomWidth: bordered ? 1 : 0,
        },
      ]}
      onPress={() => {
        if (isRevaultRequired) {
          return;
        }
        onSelect('selection', accountIdentifier);
      }}
    >
      <View style={styles.optionContent}>
        <View
          style={[
            commonStyles.row,
            { width: '100%', paddingVertical: primitive.spacing.$3, gap: primitive.spacing.$1 },
          ]}
        >
          <View
            style={[commonStyles.row, { flex: 1, flexWrap: 'wrap', gap: primitive.spacing.$4 }]}
          >
            <PaymentMethodIcon paymentMethod={option} size="md" bordered />
            <View style={styles.paymentMethodDetailsContainer}>
              <PaymentMethodDetails paymentMethod={option} displayFor="selectorOption" />
              {showMasterCardBadge && (
                <Badge
                  variant="default-subtle"
                  iconName="offer"
                  backgroundColor={primitive.bk.$meltyYellowOpacity20}
                  iconSize="$3"
                  _text={{ fontSize: '2xs' }}
                  pl="$1"
                  pr="$1"
                >
                  Unlock Offer
                </Badge>
              )}
            </View>
          </View>
          {isSelectedMethod && (
            <Icon variant="check" title="check" color={theme.token('icon-default')} size={5} />
          )}
        </View>
        {isRevaultRequired && (
          <>
            <View style={styles.dashedSeparator} />
            <View style={[commonStyles.row, styles.expiredCardContainer]}>
              <View style={[commonStyles.row, { flex: 1 }]}>
                <Icon variant="warning" size="xs" />
                <Text style={styles.expiredCardText}>{t('expiredCard')}</Text>
              </View>
              <ActionButton
                onPress={() => onSelect('update', accountIdentifier)}
                variant={ActionButtonVariants.OUTLINE}
              >
                <Text style={styles.updateButtonText}>{t('update')}</Text>
              </ActionButton>
            </View>
          </>
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  option: {
    padding: 0,
    paddingHorizontal: primitive.spacing.$2,
  },
  optionContent: {
    flexDirection: 'column',
    flex: 1,
  },
  paymentMethodDetailsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: 1,
    gap: primitive.spacing.$2,
  },
  expiredCardText: {
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
    fontSize: 12,
    marginLeft: primitive.spacing.$2,
  },
  dashedSeparator: {
    flex: 1,
    borderWidth: 1,
    borderColor: primitive.bk.$bbqBrownOpacity10,
    borderStyle: 'dashed',
  },
  expiredCardContainer: {
    gap: primitive.spacing.$1,
    flex: 1,
    width: '100%',
    paddingVertical: primitive.spacing.$2,
  },
  updateButtonText: {
    fontSize: 12,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
    paddingHorizontal: primitive.spacing.$6,
  },
});
